import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";
import { cn } from "@/lib/utils";
import { openInDocs } from "@/lib/docLinkify";

// ── QueueLane (Desk v3, Jeff 10/29: Next up + On hold, inline expand, to-top) ─
// The runway splits into two disjoint sections:
//   1. Next up — PLAN/TEST/INTATE rows, full-width, inline expandable
//   2. On hold — TABLED rows, muted, full-width
// Both use the same inline expand mechanism as the engine bay tiles.
// Priority controls: ↑/↓ and ⤒ to top (calls move:-99).

const ReadyLine = ({ row }) => {
  const p = row.payload || {};
  const blockers = String(p.blockersLine || "");
  if (row.state === "PLAN" || row.state === "TEST") {
    return blockers.replace(/^Blockers: /, "").replace(/^Waiting on: /, "") ||
      "Your word moves it into the bay — the approve gates it";
  }
  if (row.state === "INTAKE") {
    return "Taking shape — Kit works the wording in the idea dock";
  }
  return blockers || "";
};

// Inline expanded detail reused for NextUp rows (same as ProjectTile expansion)
const RowExpandedDetail = ({ row }) => {
  const p = row.payload || {};
  // Jeff 11-04: dispatched rows carry their phases in the runner (bay-twin
  // shape), chat-shaped goal rows in detail.phases — read both.
  const runnerPhases = Array.isArray(p.runner?.phases) ? p.runner.phases : [];
  const phases = runnerPhases.length > 0
    ? runnerPhases
    : Array.isArray(p.detail?.phases) ? p.detail.phases : [];
  const shape = p.chatShape || null;
  const asks = shape?.asks || [];
  const refs = (shape?.refs || []).filter((f) => f && f.path);
  const fromChat = p.fromChat?.thread || shape?.thread || null;
  const planNote = p.kit_plan
    ? p.kit_plan.state === "APPROVED"
      ? "Plan approved — the bay runs these phases."
      : "Kit proposed the plan below — approve to move it into the bay."
    : runnerPhases.length > 0
      ? "Dispatched plan — the bay runs these phases."
      : phases.length > 0
      ? "Plan on the card — Kit shapes each phase as work starts."
      : "No phases yet — the plan lands here when Kit shapes it (keep the chat moving).";
  return (
    <div className="overflow-hidden border-t border-os-border bg-os-wash/40 px-5 pb-5 pt-4 motion-safe:transition-all motion-safe:duration-300" data-testid={`desk-row-detail-${row.slug}`}>
      <div className="flex flex-col gap-4 md:flex-row md:gap-6">
        {/* LEFT â why, the conversation, files */}
        <div className="min-w-0 flex-1">
          {p.goal && (
            <p className="text-[0.8125rem] leading-[1.5] text-os-ink-2">{p.goal}</p>
          )}
          {asks.length > 0 && (
            <div className="mt-2" data-testid={`desk-row-asks-${row.slug}`}>
              <div className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">
                Shaped in chat â {asks.length} ask{asks.length === 1 ? "" : "s"}
              </div>
              <ul className="mt-1 flex flex-col gap-0.5">
                {asks.slice(0, 4).map((a, i) => (
                  <li key={i} className="truncate text-[0.8125rem] text-os-ink-2" title={a}>
                    "{a.length > 110 ? a.slice(0, 110) + "â¦" : a}"
                  </li>
                ))}
              </ul>
              {fromChat && (
                <Link
                  to={`/chat?thread=${fromChat}`}
                  data-testid={`desk-row-openchat-${row.slug}`}
                  className="mt-1.5 inline-block font-mono text-[0.6875rem] text-os-ink-3 underline-offset-2 hover:text-os-ink hover:underline"
                >
                  open the shaping chat â
                </Link>
              )}
            </div>
          )}
          {refs.length > 0 && (
            <div className="mt-2" data-testid={`desk-row-refs-${row.slug}`}>
              <div className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">
                Files referenced
              </div>
              <ul className="mt-1 flex flex-col gap-0.5">
                {refs.slice(0, 5).map((f) => (
                  <li key={f.path}>
                    <button
                      type="button"
                      onClick={() => openInDocs(f.path)}
                      className="flex items-center gap-1 w-full text-left truncate text-[0.8125rem] text-os-ink-2 underline-offset-2 hover:text-os-ink hover:underline"
                    >
                      <span className="mr-1 font-mono text-[0.6875rem] text-os-ink-4">{f.name?.endsWith(".md") ? "md" : "pdf"}</span>
                      {f.name || f.path.split("/").pop()}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {/* RIGHT â phases/plan (no progress bar â words of state) */}
        <div className="min-w-0 flex-1 md:border-l md:border-os-border md:pl-6">
          <div className="font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-ink-4">
            The plan
          </div>
          <p className="mt-1 text-[0.8125rem] font-medium text-os-ink-2">{planNote}</p>
          {phases.length > 0 ? (
            <ol className="mt-1.5 flex flex-col gap-1" data-testid={`desk-row-phases-${row.slug}`}>
              {phases.map((ph, i) => {
                const label = typeof ph === "string" ? ph : ph.title || "";
                return (
                  <li key={i} className="text-[0.8125rem] text-os-ink-2">
                    {i + 1}. {label}
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="mt-1.5 text-[0.8125rem] text-os-ink-3" data-testid={`desk-row-nophases-${row.slug}`}>
              Nothing written down yet â the asks on the left are the working list.
            </p>
          )}
          {p.next && (
            <p className="mt-2 text-[0.8125rem] text-os-ink-3">
              Next: {String(p.next).replace(/^Next: /, "")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const NextUpRow = ({ row, board, onFlash, expandedSlug, onToggleExpand }) => {
  const p = row.payload || {};
  const busy = board.pending === row.slug;
  const stWord = { PLAN: "Taking shape", TEST: "Being verified", INTAKE: "New" }[row.state] || row.state;
  const stTone = { PLAN: "amber", TEST: "amber", INTAKE: "muted" }[row.state] || "muted";
  const expanded = expandedSlug === row.slug;
  return (
    <div
      data-testid={`desk-queue-row-${row.slug}`}
      className={cn(
        "flex flex-col border-t border-os-border first:border-t-0",
        expanded && "bg-os-wash/20"
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 px-5">
        <StateWord tone={stTone}>{stWord}</StateWord>
        <button
          type="button"
          onClick={() => onToggleExpand?.(row.slug)}
          title={expanded ? "Collapse" : "Expand"}
          className="min-w-[12rem] max-w-full flex-[2] truncate text-left text-[0.875rem] font-medium text-os-ink underline-offset-2 hover:underline"
        >
          {(p.title || row.slug).replace(/^Next: /, "")}
        </button>
        <p className="hidden min-w-0 flex-[3] truncate text-[0.8125rem] text-os-ink-3 sm:block">
          {ReadyLine({ row })}
        </p>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            data-testid={`desk-queue-top-${row.slug}`}
            disabled={busy}
            onClick={async () => {
              const ok = await board.edit("reorder", row.slug, { move: -99 });
              if (ok) onFlash?.("Moved to top — it's next in line for the bay.");
            }}
            className="rounded-control border border-os-border px-2 py-1 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong disabled:opacity-40"
            title="Move to top"
          >
            ⤒
          </button>
          <button
            type="button"
            data-testid={`desk-queue-up-${row.slug}`}
            disabled={busy}
            onClick={async () => {
              const ok = await board.edit("reorder", row.slug, { move: -1 });
              if (ok) onFlash?.("Moved up — it's next in line for the bay.");
            }}
            className="rounded-control border border-os-border px-2 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
          >
            ↑
          </button>
          <button
            type="button"
            data-testid={`desk-queue-down-${row.slug}`}
            disabled={busy}
            onClick={async () => {
              const ok = await board.edit("reorder", row.slug, { move: 1 });
              if (ok) onFlash?.("Moved down.");
            }}
            className="rounded-control border border-os-border px-2 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
          >
            ↓
          </button>
        </div>
      </div>
      {expanded && <RowExpandedDetail row={row} />}
    </div>
  );
};

const OnHoldRow = ({ row, board, projectsBoard, onFlash, expandedSlug, onToggleExpand }) => {
  const p = row.payload || {};
  const busy = board.pending === row.slug;
  const expanded = expandedSlug === row.slug;
  const note = (p.jeff || {}).postponed?.note || "";
  // Jeff 11-04: dispatched rows live on the PROJECTS board (their idea is
  // archived) — verbs must go there or the button 404s silently (the exact
  // dead-park bug Jeff hit). Goals rows act on the goals board and the
  // backend mirrors to the bay twin.
  const host = row.boardSrc === "projects" ? projectsBoard || board : board;
  return (
    <div
      data-testid={`desk-hold-row-${row.slug}`}
      className={cn(
        "flex flex-col border-t border-os-border first:border-t-0",
        expanded && "bg-os-wash/20"
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 px-5">
        <StateWord tone="muted">Parked</StateWord>
        <button
          type="button"
          onClick={() => onToggleExpand?.(row.slug)}
          title={expanded ? "Collapse" : "Expand"}
          className="min-w-[12rem] max-w-full flex-[2] truncate text-left text-[0.875rem] font-medium text-os-ink underline-offset-2 hover:underline"
        >
          {(p.title || row.slug).replace(/^Next: /, "")}
        </button>
        <p className="hidden min-w-0 flex-[3] truncate text-[0.8125rem] text-os-ink-3 sm:block">
          {note ? `Parked — ${note.slice(0, 90)}` : "Parked — resume moves it back into the bay"}
        </p>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            data-testid={`desk-hold-bringback-${row.slug}`}
            disabled={busy}
            onClick={async () => {
              const res = await host.act("resume", row.slug);
              if (res && onFlash) onFlash("Back on the stack now — it waits for your word again.");
            }}
            className="rounded-control border border-os-border px-2 py-1 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong disabled:opacity-40"
          >
            bring back
          </button>
          <button
            type="button"
            data-testid={`desk-hold-archive-${row.slug}`}
            disabled={busy}
            onClick={async () => {
              const res = await host.act("archiveAny", row.slug, "Jeff archived it from the Desk — done with it here.");
              if (res && onFlash) onFlash("Archived — out of the desk. It stays archived when Kit syncs.");
            }}
            className="rounded-control border border-os-border px-2 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
            title="Move this out of the Desk entirely"
          >
            archive
          </button>
          <button
            type="button"
            data-testid={`desk-hold-top-${row.slug}`}
            disabled={busy}
            onClick={async () => {
              const ok = await host.edit("reorder", row.slug, { move: -99 });
              if (ok) onFlash?.("Moved to top of On hold.");
            }}
            className="rounded-control border border-os-border px-2 py-1 font-mono text-[0.6875rem] text-os-ink-3 hover:border-os-border-strong hover:text-os-ink disabled:opacity-40"
            title="Move to top"
          >
            ⤒
          </button>
        </div>
      </div>
      {expanded && <RowExpandedDetail row={row} />}
    </div>
  );
};

export const QueueLane = ({ goalRows, board, projectsBoard, onFlash }) => {
  const [expandedSlug, setExpandedSlug] = useState(null);
  const toggle = (slug) => setExpandedSlug((prev) => (prev === slug ? null : slug));

  const nextUp = useMemo(
    () =>
      goalRows
        .filter((r) => ["PLAN", "TEST", "INTAKE"].includes(r.state))
        .sort((a, b) => (a.sort ?? 900) - (b.sort ?? 900)),
    [goalRows],
  );

  const onHold = useMemo(
    () =>
      goalRows
        .filter((r) => r.state === "TABLED")
        .sort((a, b) => (a.sort ?? 900) - (b.sort ?? 900)),
    [goalRows],
  );

  const [showAllNext, setShowAllNext] = useState(false);
  const visibleNext = showAllNext ? nextUp : nextUp.slice(0, 5);
  const hiddenNextN = nextUp.length - visibleNext.length;

  const [showAllHold, setShowAllHold] = useState(false);
  const visibleHold = showAllHold ? onHold : onHold.slice(0, 5);
  const hiddenHoldN = onHold.length - visibleHold.length;

  return (
    <>
      <section data-testid="desk-next-up" className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex items-center gap-2">
            <Label as="h2">Next up</Label>
            {nextUp.length > 0 && (
              <span
                data-testid="desk-next-up-tag"
                className="rounded-chip border border-os-amber/40 bg-os-amber/10 px-2 py-0.5 font-mono text-[0.5625rem] uppercase tracking-[0.08em] text-os-amber"
              >
                next into the bay
              </span>
            )}
          </div>
          <MonoMeta as="p" data-testid="desk-next-up-count">
            {nextUp.length} project{nextUp.length === 1 ? "" : "s"} in line
          </MonoMeta>
        </div>
        <Card className="px-0 py-0">
          {nextUp.length === 0 ? (
            <p className="px-5 py-4 text-[0.875rem] text-os-ink-2">
              Nothing queued — say an idea in the dock and it shapes up here, or
              open <Link to="/inflight" className="underline underline-offset-2">In Flight</Link> for the full axis.
            </p>
          ) : (
            <>
              {visibleNext.map((row, i) => (
                <NextUpRow
                  key={row.slug}
                  row={row}
                  board={board}
                  onFlash={onFlash}
                  expandedSlug={expandedSlug}
                  onToggleExpand={toggle}
                  isFirst={i === 0}
                />
              ))}
              {hiddenNextN > 0 && (
                <button
                  type="button"
                  data-testid="desk-next-up-more"
                  onClick={() => setShowAllNext(true)}
                  className="px-5 py-3 font-mono text-[0.6875rem] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline"
                >
                  +{hiddenNextN} more in line — show them
                </button>
              )}
            </>
          )}
        </Card>
      </section>

      <section data-testid="desk-on-hold" className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <Label as="h2">On hold</Label>
          <MonoMeta as="p" data-testid="desk-on-hold-count">
            {onHold.length} parked
          </MonoMeta>
        </div>
        <Card className="px-0 py-0">
          {onHold.length === 0 ? (
            <p className="px-5 py-4 text-[0.875rem] text-os-ink-2">
              Nothing parked — pull one back when you want it worked.
            </p>
          ) : (
            <>
              {visibleHold.map((row) => (
                <OnHoldRow
                  key={row.slug}
                  row={row}
                  board={board}
                  projectsBoard={projectsBoard}
                  onFlash={onFlash}
                  expandedSlug={expandedSlug}
                  onToggleExpand={toggle}
                />
              ))}
              {hiddenHoldN > 0 && (
                <button
                  type="button"
                  data-testid="desk-on-hold-more"
                  onClick={() => setShowAllHold(true)}
                  className="px-5 py-3 font-mono text-[0.6875rem] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline"
                >
                  +{hiddenHoldN} more parked — show them
                </button>
              )}
            </>
          )}
        </Card>
      </section>
    </>
  );
};

export default QueueLane;
